import React, { useCallback } from 'react';
import { DashboardUser } from '../lib/transferObjects';
import { getUserAccess } from '../auth/accessControl';
import { UserSavedSearchFilters } from '../ai2/pages/searchPage/types';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentFilter, setUserSavedFilters } from '../store/ai2';
import type { RootState } from '../store';

interface LoggedInUser {
  registered: boolean;
  firstName: string;
  lastName: string;
  userName: string;
  contactEmail: string;
  passwordHashedAndEncoded?: string;
  ihi?: string;
  hpii?: string;
  displayName?: string;
  userStatus: number;
  createdAt: string;
  deletedAt?: string;
  gender?: string;
  dob?: string;
  medicare?: string;
  accessToken: string;
  medicareIrn?: string;
  middleName?: string;
  referralCode: string;
  smsConsent?: boolean;
  postcode?: string;
  episodeClosed?: boolean;
  clinic?: string;
  primaryClinicianId?: number;
  additionalClinician1Id?: number;
  additionalClinician2Id?: number;
  secretKey?: string;
  notImported?: boolean;
  phone?: string;
  features: {
    algorithms: {
      medicationInsights2: boolean;
    };
    hiddenTags: string[];
    userDashboardColumns: {
      id: string;
      enabled: boolean;
    }[];
    searchFilters: UserSavedSearchFilters[];
  };
  tenancyDetails: {
    id: number;
    user_status: 0 | 1 | null;
  }[];
  userId: number;
  roles?: string;
  tenancies: number[];
}

interface LoggedInUserContextType {
  loggedInUser: LoggedInUser | null;
  refetchLoggedInUser: () => Promise<void>;
}

const LoggedInUserContext = React.createContext<LoggedInUserContextType>({
  loggedInUser: null,
  refetchLoggedInUser: async () => {},
});

export const useLoggedInUser = () => React.useContext(LoggedInUserContext);

const LoggedInUserProvider = ({ children }: { children: React.ReactNode }) => {
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );
  const dispatch = useDispatch();
  const [loggedInUser, setLoggedInUser] = React.useState<LoggedInUser | null>(
    null,
  );

  const toLoggedInUser = (user: DashboardUser): LoggedInUser | null => {
    if (!user) {
      return null;
    }

    return {
      registered: user.registered,
      firstName: user.first_name,
      lastName: user.last_name,
      userName: user.user_name,
      contactEmail: user.contact_email,
      ihi: user.ihi,
      hpii: user.hpii,
      displayName: user.display_name,
      userStatus: user.user_status,
      createdAt: user.created_at,
      deletedAt: user.deleted_at,
      gender: user.gender,
      dob: user.dob,
      medicare: user.medicare,
      accessToken: user.access_token,
      medicareIrn: user.medicare_irn,
      middleName: user.middle_name,
      referralCode: user.referral_code,
      smsConsent: user.sms_consent,
      postcode: user.postcode,
      episodeClosed: user.episode_closed,
      clinic: user.clinic,
      primaryClinicianId: user.primary_clinician_id,
      additionalClinician1Id: user.additional_clinician1_id,
      additionalClinician2Id: user.additional_clinician2_id,
      secretKey: user.secret_key,
      notImported: user.not_imported,
      phone: user.phone,
      features: {
        algorithms: {
          medicationInsights2: user.features?.algorithms?.medicationInsights2,
        },
        hiddenTags: user.features?.hiddenTags,
        userDashboardColumns:
          user.features?.userDashboardColumns?.map((column: any) => ({
            id: column.id,
            enabled: column.enabled,
          })) || [],
        searchFilters: user.features?.searchFilters || [],
      },
      userId: user.user_id,
      roles: user.roles,
      tenancies: user.tenancies,
      tenancyDetails: user.tenancy_details,
    };
  };

  const refetchLoggedInUser = useCallback(async () => {
    try {
      const { data } = await getUserAccess(true);
      if (data) {
        setLoggedInUser(toLoggedInUser(data));
      }
    } catch (error) {
      console.error('Failed to refresh logged-in user:', error);
    }
  }, [setLoggedInUser, toLoggedInUser]);

  const initialSavedFilters = (
    user: LoggedInUser | null,
    tenancyId?: number,
  ) => {
    if (!tenancyId || !user) return;
    const tenancyFilter = user.features?.searchFilters.find(
      (item) => item.tenancyId === tenancyId,
    );
    const filter = tenancyFilter ? [...tenancyFilter.data] : [];
    dispatch(
      setUserSavedFilters(
        filter.sort((a, b) =>
          a.pinned === b.pinned ? 0 : a.pinned ? -1 : 1,
        ) || [],
      ),
    );
    const pinnedFilter = filter.find((item) => item.pinned);
    const currentFilters =
      pinnedFilter?.text.split(',').map((filter) => filter.trim()) || [];
    dispatch(setCurrentFilter([...(currentFilters || ['All Patients'])]));
  };

  React.useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const { data } = await getUserAccess();

        if (isMounted && data) {
          const user = toLoggedInUser(data);
          setLoggedInUser(user);
        }
      } catch (error: any) {
        if (isMounted) setLoggedInUser(null);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    if (selectedTenancy) {
      initialSavedFilters(loggedInUser, selectedTenancy.tenancy_id);
    }
  }, [loggedInUser, selectedTenancy]);

  const value = React.useMemo(
    () => ({ loggedInUser, refetchLoggedInUser }),
    [loggedInUser, refetchLoggedInUser],
  );
  return (
    <LoggedInUserContext.Provider value={value}>
      {children}
    </LoggedInUserContext.Provider>
  );
};

export default LoggedInUserProvider;
